// Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms and the SOW between the parties dated 10/31/2024.

import { CommonModule } from '@angular/common';
import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, ElementRef, ViewChild, AfterViewChecked, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DialogComponent } from "./dialog/dialog.component";
import { FormBuilder, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { WebSocketService, ChatResponse, ConnectionStatus } from '../services/websocket.service';
import { Subscription, filter } from 'rxjs';
import { ChatHistoryService, PromptDialog as HistoryPromptDialog } from '../services/chat-history.service';
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { Router } from '@angular/router';
import { CorrelationService } from '../services/correlation.service';

/**
 * Interface representing a dialog message in the chat
 */
interface PromptDialog {
  uuid: string;
  type: 'prompt' | 'response' | 'loading' | 'buttons' | 'list' | 'embeddedLink' | 'dashboards';
  value?: string;
  buttons?: string[];
  listItems?: string[];
  textBeforeButtons?: string;
  textAfterButtons?: string;
  dashboards?: { title: string; dashboardId: string }[];
}

/**
 * Interface for input event payloads
 */
interface InputEventPayload {
  uuid: string;
  value: string;
  initialValue: string;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, DialogComponent, ReactiveFormsModule],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  // ViewChild and Output
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @Output() close = new EventEmitter<void>();
  
  // Form related properties
  public promptForm: FormGroup;
  public formValue: string = "";
  public isSendButtonDisabled = true;
  public isProcessingMessage = false; // New property to track when waiting for response
  
  // UI state properties
  public isChatModalOpen: boolean = true;
  public isLoading: boolean = false;
  public hasExcessiveDelay: boolean = false;
  private hasRun = false;
  private responseTimer: any = null;
  
  // Chat dialog properties
  public promptDialog: PromptDialog[] = [];
  private promptQueueBeforeConnection: PromptDialog[] = [];
  private promptsToDisplayAfterTyping: PromptDialog[] = [];
  public errorResponseReceived: boolean = false;
  public errorMessage: string = '';
  
  // Connection status properties
  public ConnectionStatus = ConnectionStatus; // Make enum available to template
  public connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  public errorWithWebSocket: boolean = false;
  
  // Subscription management
  private subscriptions: Subscription = new Subscription();
  
  // Utility
  public JSON: any;
  
  // Flag to determine if we should show initial messages or load from history
  private hasHistory: boolean = false;
  private activeGenAiIntent: string = 'unknown';

  // Add a public getter for the isReconnecting property
  public get isReconnecting(): boolean {
    return this.webSocketService.isReconnecting;
  }
  
  constructor(
    private fb: FormBuilder,
    private webSocketService: WebSocketService,
    private chatHistoryService: ChatHistoryService,
    private cloudWatchRumService: CloudWatchRumService,
    private router: Router,
    private correlationService: CorrelationService
  ) {
    this.JSON = JSON;
    this.promptForm = this.fb.group({
      prompt: ['']
    });
  }

  ngOnInit(): void {
    this.cloudWatchRumService.recordPageView('/genai');

    this.setupFormSubscription();
    
    // Try to load chat history first
    this.loadChatHistory();
    
    // Only setup initial messages if there's no history
    if (!this.hasHistory) {
      this.setupInitialMessages();
    }
    
    this.setupWebSocketSubscriptions();
    
    // Establish WebSocket connection
    this.webSocketService.connect();
  }

  /**
   * Load chat history from service
   */
  private loadChatHistory(): void {
    const history = this.chatHistoryService.getHistory();
    if (history && history.length > 0) {
      this.hasHistory = true;
      this.promptDialog = history as PromptDialog[];
      console.log('Loaded chat history:', this.promptDialog.length, 'messages');
    }
  }

  /**
   * Save current chat history
   */
  private saveChatHistory(): void {
    if (this.promptDialog.length > 0) {
      this.chatHistoryService.saveHistory(this.promptDialog as HistoryPromptDialog[]);
    }
  }

  ngAfterViewChecked(): void {
    if (!this.hasRun) {
      this.scrollToBottom();
      this.hasRun = true;
    }
  }
  
  ngOnDestroy(): void {
    // Clean up response timer
    this.clearResponseTimer();
    
    // Clean up all subscriptions when component is destroyed
    this.subscriptions.unsubscribe();
  }

  /**
   * Sets up form value change subscription
   */
  private setupFormSubscription(): void {
    const formSub = this.promptForm.get('prompt')?.valueChanges.subscribe((value: string) => {
      this.formValue = value;
      this.isSendButtonDisabled = !value || value.length === 0;
    });
    
    if (formSub) {
      this.subscriptions.add(formSub);
    }
  }
  
  /**
   * Sets up initial greeting messages
   */
  private setupInitialMessages(): void {
    this.addLoadingIndicator();
    
    // Initial greeting
    this.promptQueueBeforeConnection.push({
      uuid: 'initialGreeting',
      type: 'response',
      value: 'Hi there! Welcome to AI Assistant powered by GenAI. We are here to help you manage and generate reports with ease. Pick from one of the options below to get started.'
    });
    
    // Initial buttons
    this.promptQueueBeforeConnection.push({
      uuid: 'initialButtons',
      type: 'buttons',
      value: '',
      buttons: ['Find a report', 'Modify a report'] // ARAP-662 'Create a report' button removed.  Can be readded here when ready.
    });
  }
  
  /**
   * Sets up WebSocket message and connection status subscriptions
   */
  private setupWebSocketSubscriptions(): void {
    // Subscribe to WebSocket messages
    const messageSub = this.webSocketService.messages$
      .pipe(filter(message => message !== null))
      .subscribe(message => {
        if (message) {
          this.handleWebSocketMessage(message);
        }
      });
    this.subscriptions.add(messageSub);
    
    // Subscribe to connection status updates
    const connectionSub = this.webSocketService.connectionStatus$
      .subscribe(status => this.handleConnectionStatusChange(status));
    this.subscriptions.add(connectionSub);
  }
  
  /**
   * Handles WebSocket connection status changes
   */
  private handleConnectionStatusChange(status: ConnectionStatus): void {
    this.connectionStatus = status;
    this.errorWithWebSocket = false; // Reset error state on status change
    console.log('WebSocket connection status:', status);
    
    if (status === ConnectionStatus.CONNECTED) {
      this.handleSuccessfulConnection();
    } else if (status === ConnectionStatus.ERROR || status === ConnectionStatus.DISCONNECTED) {
      this.errorWithWebSocket = true;
      this.isProcessingMessage = false; // Re-enable input on connection loss
      // Clear the response timer on error or disconnect
      this.clearResponseTimer();
      this.hasExcessiveDelay = false;
    }
  }
  
  /**
   * Process queued messages after successful WebSocket connection
   */
  private handleSuccessfulConnection(): void {
    // If we have history, don't process the initial messages
    if (this.hasHistory) {
      return;
    }
    
    // Remove loading indicators
    this.promptDialog = this.promptDialog.filter((dialog: PromptDialog) => dialog.type !== 'loading');
    
    // Process queued messages
    this.promptQueueBeforeConnection.forEach(dialog => {
      if (dialog.type === 'response' && dialog.value) {
        this.applyTypingEffectToMessage(dialog.value);
      } else {
        this.promptsToDisplayAfterTyping.push(dialog);
      }
    });
    
    // Clear the queue after processing
    this.promptQueueBeforeConnection = [];
    
    // Scroll to bottom after processing initial messages
    this.scrollToBottom();
  }
  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(response: ChatResponse): void {
    // Clear response timer and reset delay flag
    this.clearResponseTimer();
    this.hasExcessiveDelay = false;
    this.errorResponseReceived = false; // Reset error state on new message
    this.errorMessage = ''; // Clear any previous error message
    this.isProcessingMessage = response.type === 'IN_PROGRESS'; // Re-enable input after response is received
  
    // Handle error responses by showing an f-alert in UI
    if (response.type === 'ERROR') {
      console.error('Received error response:', response);
      this.errorMessage = response.message;
      this.errorResponseReceived = true;
      this.recordGenAiOutcome('failure', response);
      return;
    }
  
    // Verify that this is an agent response
    if (response.type !== 'SUCCESS' && response.type !== 'IN_PROGRESS' ) {
      console.warn('Received non-agent response:', response);
      this.errorResponseReceived = true;
      this.recordGenAiOutcome('failure', response);
      return;
    }
    this.recordGenAiOutcome(response.type === 'IN_PROGRESS' ? 'in_progress' : 'success', response);
    
    
    // Remove any loading indicators
    this.promptDialog = this.promptDialog.filter(dialog => dialog.type !== 'loading');
    
    // Create a unique ID for the message
    const messageId = `response_${this.getTimestampString(response.timestamp)}`;
    
    try {
      // Try to parse the response as JSON first
      const parsedResponse = this.JSON.parse(response.message);
      
      if (parsedResponse.options && Array.isArray(parsedResponse.options)) {
        this.recordGenAiStructuredOutcome('options_returned', parsedResponse.options.length);
        this.handleOptionsResponse(messageId, parsedResponse);
        return;
      }

      if (parsedResponse.dashboards && Array.isArray(parsedResponse.dashboards)) {
        this.recordGenAiStructuredOutcome('reports_found', parsedResponse.dashboards.length);
        this.handleDashboardsResponse(messageId, parsedResponse);
        return;
      }
      
      if (parsedResponse.message) {
        this.displayTypingEffect(messageId, parsedResponse.message, parsedResponse.list);
        return;
      }

      if (parsedResponse.dashboardName) {
        this.cloudWatchRumService.addCustomEvent('genai_report_modify_outcome', {
          intent: this.activeGenAiIntent,
          outcome: 'dashboard_link_returned',
          dashboardName: parsedResponse.dashboardName,
          dashboardId: parsedResponse.dashboardId || null,
        });
        this.displayDashboardLink(messageId, parsedResponse);
        return;
      }

    } catch (error) {
      // Not a JSON response, handle as plain text
      console.debug('Handling as plain text response');
    }
    
    // Handle as plain text if not structured JSON
    this.displayTypingEffect(messageId, response.message);
  }
  
  /**
   * Format timestamp to string
   */
  private getTimestampString(timestamp: any): string {
    return typeof timestamp === 'number' 
      ? timestamp.toString() 
      : timestamp;
  }
  
  /**
   * Handle structured response with options/buttons
   */
  private handleOptionsResponse(messageId: string, parsedResponse: any): void {
    this.hasRun = false;
    this.promptDialog.push({
      uuid: messageId,
      type: 'buttons',
      value: '',
      buttons: parsedResponse.options
    });
    this.isLoading = false;
    this.scrollToBottom();
    
    // Save chat history after message is added
    this.saveChatHistory();
  }

  /**
   * Handle structured response with dashboards
   */
  private handleDashboardsResponse(messageId: string, parsedResponse: any): void {
    this.hasRun = false;
    this.promptDialog.push({
      uuid: messageId,
      type: 'dashboards',
      value: '',
      dashboards: parsedResponse.dashboards,
    });
    this.isLoading = false;
    this.scrollToBottom();
    
    // Save chat history after message is added
    this.saveChatHistory();
  }

  /**
   * Handle embedded link
   */
  private displayDashboardLink(messageId: string, parsedResponse: any): void {
    this.hasRun = false;
    this.promptDialog.push({
      uuid: messageId,
      type: 'embeddedLink',
      value: parsedResponse
    });
    this.isLoading = false;
    this.scrollToBottom();
    
    // Save chat history after message is added
    this.saveChatHistory();
  }
  
  /**
   * Display typing effect for a message
   */
  private displayTypingEffect(messageId: string, message: string, listItems?: string[]): void {
    // Create a placeholder for the message
    this.promptDialog.push({
      uuid: messageId,
      type: 'response',
      value: ''
    });
    
    const loadingIndex = this.promptDialog.length - 1;
    const words = message.split(' ');
    const blocks = Math.ceil(words.length / 5);
    let currentBlock = 0;
    
    const interval = setInterval(() => {
      if (currentBlock < blocks) {
        // Add next block of words
        const start = currentBlock * 5;
        const end = start + 5;
        const block = words.slice(start, end).join(' ');
        
        // Update the message with the new block
        this.promptDialog[loadingIndex].value += block + ' ';
        this.scrollToBottom();
        currentBlock++;
      } else {
        // Add list items if provided
        if (listItems) {
          this.promptDialog.push({  
            uuid: `${messageId}_list`,
            type: 'list',
            listItems: listItems,
          });
        }
        
        // Add any additional content waiting to be displayed
        if (messageId.includes('response_')) {
          this.promptsToDisplayAfterTyping.forEach(dialog => {
            this.promptDialog.push(dialog);
          });
          this.promptsToDisplayAfterTyping = [];
        }
        
        this.hasRun = false;
        this.scrollToBottom();
        clearInterval(interval);
        
        // Save chat history after the typing effect completes
        this.saveChatHistory();
      }
    }, 100);
    
    this.isLoading = false;
  }
  
  /**
   * Apply typing effect to a message (used for initial messages)
   */
  private applyTypingEffectToMessage(message: string): void {
    const messageId = `response_${Math.random().toString(36).substring(2, 15)}`;
    this.displayTypingEffect(messageId, message);
  }
  /**
   * Handle textarea value change
   */
  onTextareaChanged(event: Event): void {
    const payload = (event as CustomEvent<InputEventPayload>).detail;
    
    // Update the form control value
    this.promptForm.get('prompt')?.setValue(payload.value);
    this.formValue = payload.value;
    this.isSendButtonDisabled = !this.formValue || this.formValue.length === 0;
  }

  /**
   * Handle textarea blur event
   */
  onTextareaBlur(event: Event): void {
    // Can be implemented if needed
  }

  /**
   * Handle textarea focus event
   */
  onTextareaFocused(event: Event): void {
    // Can be implemented if needed
  }

  /**
   * Handle button click event
   */
  onButtonClicked(event: Event): void {
    if (this.formValue?.length > 0) {
      this.hasRun = false;
      this.sendResponse(event);
    }
  }

  /**
   * Handle predefined button selection
   */
  onClickEvent(buttonText: string): void {
    // Remove existing buttons
    this.promptDialog = this.promptDialog.filter(dialog => dialog.type !== 'buttons');
    this.isLoading = true;
    this.isProcessingMessage = true; // Disable input while waiting for response
    
    // Add user prompt to dialog
    const userMessageId = `user_${Date.now()}`;
    this.promptDialog.push({
      uuid: userMessageId,
      type: 'prompt',
      value: buttonText
    });
    
    // Add loading indicator
    this.addLoadingIndicator();
    
    // Start response timer
    this.startResponseTimer();
    
    // Send message through WebSocket
    this.recordGenAiPrompt(buttonText, 'button');
    this.webSocketService.sendMessage(buttonText);
    this.scrollToBottom();
    
    // Save chat history after user message is added
    this.saveChatHistory();
  }

  /**
   * Start timer to track response delay
   */
  private startResponseTimer(): void {
    // Clear any existing timer first
    this.clearResponseTimer();
    
    // Set a new timer for 15 seconds
    this.responseTimer = setTimeout(() => {
      this.hasExcessiveDelay = true;
      console.warn('WebSocket response delay exceeded 15 seconds');
    }, 15000);
  }
  
  /**
   * Clear response timer if it exists
   */
  private clearResponseTimer(): void {
    if (this.responseTimer) {
      clearTimeout(this.responseTimer);
      this.responseTimer = null;
    }
  }

  onEmbeddedLinkClick(dashboardId: string): void {
    const dashboardLinks = this.promptDialog.find(dialog => dialog.type === 'dashboards' && dialog.dashboards?.some(dashboard => dashboard.dashboardId === dashboardId));
    const dashboardLink = dashboardLinks?.dashboards?.find(dashboard => dashboard.dashboardId === dashboardId);
    const dashboardTitle = dashboardLink?.title || 'Dashboard';
    const correlationId = this.correlationService.create();
    this.cloudWatchRumService.addCustomEvent('genai_report_opened', {
      intent: this.activeGenAiIntent,
      dashboardId,
      dashboardTitle,
      source: 'genai_embedded_link',
      correlationId,
    });
    this.cloudWatchRumService.addCustomEvent('report_opened', {
      intent: this.activeGenAiIntent,
      dashboardId,
      contentTitle: dashboardTitle,
      source: 'genai',
      correlationId,
    });

    this.router.navigate(['/embedded-dashboard', dashboardId], {
      queryParams: {
        title: dashboardTitle,
        correlationId,
        origin: 'genai',
      },
    });

    // Visually close the panel so the newly navigated report isn't blurred/covered by the still-open chat overlay.
    // The chat component instance and its history stay mounted (see app.component.html), so this does not reset the journey.
    this.onClose();
  }

  /**
   * Add loading indicator to chat
   */
  private addLoadingIndicator(): void {
    this.isLoading = true;
    this.promptDialog.push({
      uuid: `loading-${this.promptDialog.length}`,
      type: 'loading',
      value: 'Loading...'
    });
  }

  /**
   * Handle WebSocket disabled error
   */
  private handleWebSocketDisabledError(): void {
    // Remove loading indicator
    this.promptDialog = this.promptDialog.filter((dialog: PromptDialog) => dialog.type !== 'loading');
    this.isLoading = false;
    
    // Add error message
    this.promptDialog.push({
      uuid: `error-${Date.now()}`,
      type: 'response',
      value: 'The AI Assistant is not available in this environment.'
    });
  }

  /**
   * Send user message to backend
   */
  private sendResponse(event: Event): void {
    this.isLoading = true;
    this.isProcessingMessage = true; // Disable input while waiting for response
    const payload = (event as CustomEvent<InputEventPayload>).detail;

    // Remove any button options
    this.promptDialog = this.promptDialog.filter(dialog => dialog.type !== 'buttons');

    // Add user message to promptDialog
    this.promptDialog.push({
      uuid: payload.uuid || `user_${Date.now()}`,
      type: 'prompt',
      value: this.formValue
    });
    
    // Add loading indicator
    this.addLoadingIndicator();
    
    // Start response timer
    this.startResponseTimer();
    
    // Send message through WebSocket
    this.recordGenAiPrompt(this.formValue, 'free_text');
    this.webSocketService.sendMessage(this.formValue);
    
    // Save chat history after user message is added
    this.saveChatHistory();
    
    this.clearForm();
  }

  /**
   * Reset form after sending message
   */
  private clearForm(): void {
    this.promptForm.reset();
    this.formValue = '';
    this.isSendButtonDisabled = true;
  }

  private recordGenAiPrompt(message: string, source: 'button' | 'free_text'): void {
    const resolvedIntent = this.resolveGenAiIntent(message);
    const stage = this.resolveGenAiStage(message, resolvedIntent);
    // Contextual follow-ups (selection/confirmation/save) keep the active Modify Report intent instead of resetting to unknown
    const intent = resolvedIntent !== 'unknown' ? resolvedIntent : this.activeGenAiIntent;

    this.cloudWatchRumService.addCustomEvent('genai_journey_step', {
      intent,
      action: 'prompt_submitted',
      stage,
      source,
      promptLength: message.length,
      hasAttributeIntent: intent.includes('attribute'),
      hasFilterIntent: intent.includes('filter'),
    });

    // A completed save ends the journey, so the next unrelated prompt starts with a clean context
    this.activeGenAiIntent = stage === 'save' ? 'unknown' : intent;
  }

  private recordGenAiOutcome(outcome: 'success' | 'failure' | 'in_progress', response: ChatResponse): void {
    this.cloudWatchRumService.addCustomEvent('genai_journey_outcome', {
      intent: this.activeGenAiIntent,
      outcome,
      responseType: response.type,
      responseLength: response.message?.length || 0,
    });
  }

  private recordGenAiStructuredOutcome(outcome: string, itemCount: number): void {
    this.cloudWatchRumService.addCustomEvent('genai_report_search_outcome', {
      intent: this.activeGenAiIntent,
      outcome,
      itemCount,
    });
  }

  /**
   * Classifies a prompt within the active GenAI journey so contextual follow-ups
   * (report/filter/attribute selections, yes/no confirmations, done/save) are not
   * reported as unknown once a Find/Modify Report journey is already in progress.
   */
  private resolveGenAiStage(message: string, resolvedIntent: string): 'new_request' | 'confirmation' | 'save' | 'selection' | 'unclassified' {
    if (resolvedIntent !== 'unknown') {
      return 'new_request';
    }

    const normalizedMessage = (message || '').trim().toLowerCase();

    if (/^(yes|y|no|n|confirm|confirmed|correct|that works|looks good|proceed)\.?$/.test(normalizedMessage)) {
      return 'confirmation';
    }

    if (/^(done|save|apply|finish|finished|complete|completed)\.?$/.test(normalizedMessage)) {
      return 'save';
    }

    if (this.activeGenAiIntent !== 'unknown') {
      return 'selection';
    }

    return 'unclassified';
  }

  private resolveGenAiIntent(message: string): string {
    const normalizedMessage = (message || '').toLowerCase();
    const hasReportTerm = /(report|dashboard|tile|visual|chart)/.test(normalizedMessage);
    const hasFindTerm = /(find|search|show|open|list|look up|lookup|get|pull up|display)/.test(normalizedMessage);
    const hasModifyTerm = /(modify|change|update|edit|customize|adjust|revise)/.test(normalizedMessage);
    const hasAddTerm = /(add|include|create|apply|insert|put|with)/.test(normalizedMessage);
    const hasRemoveTerm = /(remove|delete|exclude|clear|without|drop)/.test(normalizedMessage);
    const hasAttributeTerm = /(attribute|column|field|metric|dimension|measure)/.test(normalizedMessage);
    const hasFilterTerm = /(filter|where|only|for client|client|date range|status|location|region|segment)/.test(normalizedMessage);

    if (hasAddTerm && hasAttributeTerm) {
      return 'add_attribute';
    }

    if (hasRemoveTerm && hasAttributeTerm) {
      return 'remove_attribute';
    }

    if (hasAddTerm && hasFilterTerm) {
      return 'add_filter';
    }

    if (hasRemoveTerm && hasFilterTerm) {
      return 'remove_filter';
    }

    if (hasModifyTerm && hasReportTerm) {
      return 'modify_report';
    }

    if (hasFindTerm && hasReportTerm) {
      return 'find_report';
    }

    return 'unknown';
  }

  /**
   * Scroll chat to bottom
   */
  scrollToBottom(): void {
    try {
      setTimeout(() => {
        if (this.chatContainer && this.chatContainer.nativeElement) {
          this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
        }
      }, 30);
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }
    /**
   * Close chat modal
   */
  onClose(): void {
    // Clean up any pending requests and remove loading indicators
    this.promptDialog = this.promptDialog.filter((dialog: PromptDialog) => dialog.type !== 'loading');
    this.isProcessingMessage = false; // Reset the flag when closing
    
    // Save chat history before closing
    this.saveChatHistory();
    
    this.close.emit();
  }

  /**
   * Check if dialog is the last response in the list
   */
  isLastResponse(index: number): boolean {
    // Check if the current dialog is a response
    if (this.promptDialog[index]?.type !== 'response' && this.promptDialog[index]?.type !== 'embeddedLink') {
      return false;
    }

    if (this.isLoading) {
      return false;
    }
    
    // Check if there are any other responses after this one
    for (let i = index + 1; i < this.promptDialog.length; i++) {
      if (this.promptDialog[i]?.type === 'response') {
        return false;
      }
    }
    
    // Additional check: if there are buttons after this response, don't show sparkles
    // The sparkles should only appear on the very last interactive element
    for (let i = index + 1; i < this.promptDialog.length; i++) {
      if (this.promptDialog[i]?.type === 'buttons' || 
          this.promptDialog[i]?.type === 'dashboards' || 
          this.promptDialog[i]?.type === 'embeddedLink') {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Check if dialog is the absolute last item in the conversation
   */
  isLastItem(index: number): boolean {
    if (this.isLoading) {
      return false;
    }
    
    return index === this.promptDialog.length - 1;
  }
  
  /**
   * Check if this dialog is the last interactive element in the conversation
   */
  isLastInteractiveElement(index: number): boolean {
    if (this.isLoading) {
      return false;
    }
    
    // For response and embeddedLink types, use the existing logic
    if (this.promptDialog[index]?.type === 'response' || this.promptDialog[index]?.type === 'embeddedLink') {
      return this.isLastResponse(index);
    }
    
    // For interactive elements (buttons, dashboards), check if this is the last dialog
    if (this.promptDialog[index]?.type === 'buttons' || this.promptDialog[index]?.type === 'dashboards') {
      return index === this.promptDialog.length - 1;
    }
    
    return false;
  }
  
  /**
   * Check if there are interactive elements (buttons, dashboards) after the current dialog
   */
  hasInteractiveElementsAfter(currentIndex: number): boolean {
    // Check if there are any interactive elements (buttons, dashboards) after this index
    for (let i = currentIndex + 1; i < this.promptDialog.length; i++) {
      if (this.promptDialog[i]?.type === 'buttons' || this.promptDialog[i]?.type === 'dashboards') {
        return true;
      }
    }
    return false;
  }
  
  /**
   * Check if this is an initial message (part of the welcome/setup flow)
   */
  isInitialMessage(index: number): boolean {
    // Consider the first few dialogs as initial messages
    // This includes the welcome message and initial options
    return index <= 2 && this.promptDialog.length <= 4;
  }
  
  /**
   * Clear chat history and establish new WebSocket connection
   */
  clearChatHistory(): void {
    this.chatHistoryService.clearHistory();
    this.promptDialog = [];
    this.hasHistory = false;
    this.setupInitialMessages();
    
    // Prevent error message during reconnection
    this.errorResponseReceived = false;
    this.errorMessage = ''; // Clear any error message
    
    // Disconnect and reconnect WebSocket to establish a new session
    this.webSocketService.disconnect();
    this.webSocketService.connect();
  }
}
